import { css } from 'styled-components';
import SVGInline from 'components/SVGInline/SVGInline';
import { styled } from 'components/ThemeConfigConnector/ThemeConfigConnnector';
import { buttonZIndex } from 'style/styled-components/cssGlobalVariables';
import { Breakpoints, MQ, MQ_NOT_TOUCH } from 'style/styled-components/cssMediaQueries';
import { hexToRgba } from 'utils/fnColors';

export const paddingMobile = 16;
export const paddingTablet = 32;
export const paddingDesktop = 40;

export const cardMargin = 8;

export const ARROW_ANCHOR = 'data-arrow-anchor';

export const ArrowIcon = styled(SVGInline)`
    width: auto;
    height: auto;
    display: inline-block;
    position: absolute;
    font-size: 0;
    top: 50%;
    transform: translateY(-50%);

    svg {
        transition: transform 0.2s ease-in-out 0s;
        width: 12px;
        height: 17px;

        path {
            fill: ${(props) => props.theme.brandedConfig.high_contrast};
        }
    }
`;

export const ArrowWrapper = styled.div`
    position: absolute;
    display: flex;
    width: 100%;
    height: 36px; /* NavigationArrow height */
    align-items: center;
    pointer-events: none;
`;

export const NavigationArrow = styled.div<{ alwaysOn: boolean; active?: boolean; isEditorialPill?: boolean }>`
    cursor: pointer;
    position: absolute;
    display: block;
    z-index: ${buttonZIndex};
    height: 36px;
    width: 36px;
    opacity: ${(props) => (props.alwaysOn ? (props.active ? '1' : '0.5 !important') : '0')};
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.5);
    outline: none;
    margin: 0;
    padding: 0;
    border-width: initial;
    border-style: none;
    border-color: initial;
    border-image: initial;
    border-radius: 50%;
    transition:
        opacity 0.2s ease-in-out 0s,
        background-color 0.2s ease-in-out;
    background-color: var(--color-dark-foreground-5);
    /* top: ${(props) => (props.isEditorialPill ? '60%' : '50%')}; */ /* Changed from 40% to 50% */
    margin: 15px;
    pointer-events: auto;

    ${MQ_NOT_TOUCH} {
        &:hover {
            background-color: var(--color-dark-foreground-15);

            ${ArrowIcon} {
                svg {
                    transform: scale(1.2);
                }
            }
        }
    }
`;

export const ArrowLeft = styled(NavigationArrow)`
    left: 0;
    ${ArrowIcon} {
        left: 12px;
    }
`;

export const ArrowRight = styled(NavigationArrow)`
    right: 0;
    ${ArrowIcon} {
        right: 12px;
    }
`;

export const CardWrapper = styled.div<{
    height: number | 'auto';
    width: number | 'auto';
    withDescription?: boolean;
    assetType?: string;
    variantCss?: ReturnType<typeof css>;
}>`
    display: inline-block;
    vertical-align: top;
    scroll-snap-align: start;
    scroll-snap-stop: normal;
    margin: ${cardMargin}px;
    margin-bottom: ${(props) => (props.assetType === 'movie' || props.assetType === 'program' ? '38px' : `${cardMargin}px`)};
    width: ${(props) => (props.width === 'auto' ? 'auto' : `${props.width}px`)};
    height: ${(props) => (props.height === 'auto' ? 'auto' : `${props.height}px`)};
    position: relative;

    ${(props) => props.variantCss}

    ${(props) =>
        props.withDescription
            ? css`
                  margin-bottom: 64px;

                  ${MQ(Breakpoints.xxs)} {
                      margin-bottom: 200px;
                  }

                  ${MQ(Breakpoints.xs)} {
                      margin-bottom: 0px;
                      margin-left: 8px;
                      margin-right: 8px;
                  }

                  ${MQ(Breakpoints.s)} {
                      margin-bottom: 50px;
                      display: inline-block;
                  }
              `
            : ''}

    &.gallery-tile {
        width: 100%;
        height: auto;
        padding-left: 0;
    }
`;

export const LaneWrapper = styled.div`
    width: 100%;
    height: auto;
    position: relative;

    ${MQ_NOT_TOUCH} {
        &:hover {
            ${ArrowLeft}, ${ArrowRight} {
                opacity: 1;
            }
        }
    }

    &.gallery-navigation-icon-style {
        ${NavigationArrow} {
            background: ${(props) => hexToRgba(props.theme.brandedConfig.high_contrast, 0)};
            border-radius: 4px;
            height: 32px;
            width: 32px;
            transform: none;
            top: auto;
            right: auto;
            left: auto;
            bottom: 105px;
            transition: all 0.2s ease-in-out 0s;

            ${ArrowIcon} {
                svg {
                    height: 20px;
                }
            }

            &:hover {
                background: ${(props) => hexToRgba(props.theme.brandedConfig.high_contrast, 0.2)};
            }
        }

        ${ArrowLeft} {
            right: 96px;
        }

        ${ArrowRight} {
            right: 40px;
        }
    }
`;

export const LaneScroller = styled.div<{ useSnap: boolean; scrollable: boolean; isEditorialPill?: boolean }>`
    height: 100%;
    padding: ${(props) => (props.isEditorialPill ? '0px 8px' : `16px ${paddingMobile}px`)};
    overflow-y: hidden;
    overflow-x: ${(props) => (props.scrollable ? 'scroll' : 'hidden')};
    margin-top: ${(props) => (props.isEditorialPill ? '24px' : '-12px')};
    margin-bottom: ${(props) => (props.isEditorialPill ? '16px' : '0')};
    white-space: nowrap;
    width: auto;
    text-align: left;
    scrollbar-width: none;
    -ms-overflow-style: none;

    ::-webkit-scrollbar {
        display: none;
        width: 0 !important;

        -webkit-appearance: none;
    }

    &.smooth {
        scroll-behavior: smooth;
    }

    ${(props) =>
        props.useSnap
            ? css`
                  scroll-padding-left: ${props.isEditorialPill ? 8 : paddingMobile}px;
                  scroll-snap-type: x mandatory;
                  -webkit-overflow-scrolling: touch;
                  ${MQ(Breakpoints.s)} {
                      scroll-padding-left: ${props.isEditorialPill ? 8 : paddingTablet}px;
                  }

                  ${MQ(Breakpoints.m)} {
                      scroll-padding-left: ${props.isEditorialPill ? 8 : paddingDesktop}px;
                  }
              `
            : css`
                  @-moz-document url-prefix() {
                      &:after {
                          content: '';
                          display: inline-block;
                          width: ${props.isEditorialPill ? 8 : paddingMobile}px;

                          ${MQ(Breakpoints.s)} {
                              width: ${props.isEditorialPill ? 8 : paddingTablet}px;
                          }

                          ${MQ(Breakpoints.m)} {
                              width: ${props.isEditorialPill ? 8 : paddingDesktop}px;
                          }
                      }
                  }
              `}

    ${MQ(Breakpoints.s)} {
        padding: ${(props) => (props.isEditorialPill ? `0px ${paddingTablet}px` : `16px ${paddingTablet}px`)};
        margin-top: ${(props) => (props.isEditorialPill ? '0' : '-12px')};
        margin-bottom: ${(props) => (props.isEditorialPill ? '40px' : '0')};
    }

    ${MQ(Breakpoints.m)} {
        padding: ${(props) => (props.isEditorialPill ? `0px ${paddingDesktop}px` : `16px ${paddingDesktop}px`)};
    }
`;

export const ArrowsWrapper = styled.div<{ stripeType: string }>`
    position: absolute;
    top: ${(props) => (props.stripeType === 'seasons' ? '-9px' : '24px')};
    display: flex;
    align-items: center;
    width: 100%;
    height: ${(props) => (props.stripeType === 'portrait' ? '262px' : props.stripeType === 'seasons' ? '0' : '162px')};
    pointer-events: none;

    ${NavigationArrow} {
        pointer-events: all;
    }
`;
